/**
 * Size Guide Modal Logic
 * Refactored as a Custom Element to support AJAX product toggling.
 */
class SizeGuideModal extends HTMLElement {
  constructor() {
    super();
    this.state = {
      unit: 'cm',
      region: 'US'
    };
  }

  connectedCallback() {
    console.log('Size Guide: Connected');
    this.modal = this.querySelector('.size-guide-modal');
    if (!this.modal) return;

    this.dataElement = this.querySelector('script[type="application/json"]');
    if (!this.dataElement) {
      console.warn('Size Guide: Data element not found');
      return;
    }

    try {
      this.config = JSON.parse(this.dataElement.textContent);
    } catch (e) {
      console.error('Size Guide Data Parse Error:', e);
      return;
    }

    this.tableBody = this.querySelector('#SizeGuideTable tbody');
    this.headerUnit = this.querySelector('[data-header-unit]');
    this.headerRegion = this.querySelector('[data-header-region]');
    this.regionSelect = this.querySelector('#SizeGuideRegion');
    this.unitButtons = this.querySelectorAll('.size-guide-modal__unit-btn');
    this.isImageMode = this.modal.classList.contains('has-image');

    this.init();
  }

  disconnectedCallback() {
    if (this._onButtonClick) {
      document.removeEventListener('click', this._onButtonClick);
    }
  }

  init() {
    // Open triggers - delegated to document but scoped to relevant section
    this._onButtonClick = (e) => {
      const trigger = e.target.closest('[data-open-size-guide]');
      if (trigger) {
        const triggerSection = trigger.closest('.section');
        const modalSection = this.closest('.section');
        
        // If we're in the same section, or there's no clear section boundary
        if (!triggerSection || !modalSection || triggerSection === modalSection) {
          e.preventDefault();
          this.open();
        }
      }
    };
    document.addEventListener('click', this._onButtonClick);

    // Close triggers
    this.modal.querySelectorAll('[data-close-size-guide]').forEach(btn => {
      btn.addEventListener('click', () => this.close());
    });

    // Close on Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.classList.contains('is-open')) {
        this.close();
      }
    });

    // Unit toggle
    this.unitButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const unit = btn.dataset.unit;
        this.setUnit(unit);
      });
    });

    // Region toggle
    if (this.regionSelect) {
      this.regionSelect.addEventListener('change', (e) => {
        this.setRegion(e.target.value);
      });
    }

    // Initial render
    this.render();
  }

  open() {
    this.modal.classList.add('is-open');
    this.modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('overflow-hidden');
    this.render(); // Re-render on open to be sure
  }

  close() {
    this.modal.classList.remove('is-open');
    this.modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('overflow-hidden');
  }

  setUnit(unit) {
    this.state.unit = unit;
    this.unitButtons.forEach(btn => {
      btn.classList.toggle('active', btn.dataset.unit === unit);
    });
    this.render();
  }

  setRegion(region) {
    this.state.region = region;
    this.render();
  }

  render() {
    if (!this.config || !this.config.data) {
      return;
    }

    const type = (this.config.activeType || 'men').trim().toLowerCase();
    const chartData = this.config.data[type];
    const { unit, region } = this.state;

    if (!chartData || !Array.isArray(chartData)) {
      if (this.tableBody) this.tableBody.innerHTML = '<tr><td colspan="2">No data available</td></tr>';
      return;
    }

    // Update headers
    if (this.headerUnit) {
      this.headerUnit.textContent = `Foot Length (${unit})`;
    }
    if (this.headerRegion) {
      this.headerRegion.textContent = region;
    }

    // Build rows
    const rows = chartData.map(row => {
      const footLength = row[unit] || '';
      const regionSize = row[region.toLowerCase()] || '';
      return `
        <tr>
          <td>${footLength}</td>
          <td>${regionSize}</td>
        </tr>
      `;
    }).join('');

    if (this.tableBody) {
      this.tableBody.innerHTML = rows;
    }
  }
}

if (!customElements.get('size-guide-modal')) {
  customElements.define('size-guide-modal', SizeGuideModal);
}

